import {
  BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD,
  EmploymentType,
  PensionType,
  Period,
  isPensionerIncomeValidationRequired,
} from '@brighte/validation';
import { INCOME_VERIFICATION, LABELS, convertAmountToMonthlyPeriod, productIds } from '../../utils';

const { INCOME_AFTER_TAX, ADDITIONAL_INCOME_AFTER_TAX, TOTAL_NET_INCOME, BNPL_INCOME_AFTER_TAX } = LABELS;
const {
  INCOME_AFTER_TAX_DESCRIPTION,
  INCOME_DOCUMENT_DESCRIPTION,
  SKIP_DOCUMENTS_WARNING,
  BANK_STATEMENT_INFO,
  BANK_STATEMENT_SELF_EMPLOYED_INFO,
  BANK_STATEMENT_PENSIONER_INFO,
} = INCOME_VERIFICATION;

const labels = {
  primary: {
    income: INCOME_AFTER_TAX,
    incomeDescription: INCOME_AFTER_TAX_DESCRIPTION,
    incomeDocumentDescription: INCOME_DOCUMENT_DESCRIPTION,
    skipDocumentWarning: SKIP_DOCUMENTS_WARNING,
  },
  additional: {
    income: ADDITIONAL_INCOME_AFTER_TAX,
    incomeDescription: INCOME_AFTER_TAX_DESCRIPTION,
    incomeDocumentDescription: INCOME_DOCUMENT_DESCRIPTION,
    skipDocumentWarning: SKIP_DOCUMENTS_WARNING,
  },
};
export const incomeVerificationInfo = {
  [EmploymentType.EMPLOYED]: BANK_STATEMENT_INFO,
  [EmploymentType.SELF_EMPLOYED]: BANK_STATEMENT_SELF_EMPLOYED_INFO,
  [EmploymentType.PENSIONER]: BANK_STATEMENT_PENSIONER_INFO,
};
export const getFieldLabels = (type: 'primary' | 'additional') => labels[type];

export const getIncomeOptions = (financialProductId: string): { label: string; value: EmploymentType }[] => {
  const { INCOME_OPTIONS } = INCOME_VERIFICATION;

  const defaultOptions = INCOME_OPTIONS.filter(option => option.value !== EmploymentType.OTHER);

  if (financialProductId === productIds.FLEX) {
    return defaultOptions.filter(option => option.value !== EmploymentType.PENSIONER);
  }

  if (financialProductId === productIds.ELECTRIFY_2515) {
    return INCOME_OPTIONS;
  }

  return defaultOptions;
};

export enum IncomeInputType {
  MANUAL = 'MANUAL',
  FISKIL = 'FISKIL',
}
// Define strategies for different products
export interface IncomeVerificationStrategy {
  isIncomeVerificationRequired(params: useIncomeVerificationConditionsProps): boolean;
  isAdditionalVerificationRequired(
    params: useIncomeVerificationConditionsProps & { isIncomeVerificationRequired: boolean },
  ): boolean;
}

export type useIncomeVerificationConditionsProps = {
  financialProductId: string;
  applicantPrimaryIncomeAmount: number;
  applicantPrimaryIncomePeriod: Period;
  occupationIncomeThreshold: number;
  isDocumentsRequired: boolean;
  financeAmount: number;
  employmentType: EmploymentType;
  pensionerType?: PensionType;
  dateOfBirth: Date;
  isCashApplication: boolean;
};

const DefaultStrategy: IncomeVerificationStrategy = {
  isIncomeVerificationRequired(params) {
    return params.isDocumentsRequired;
  },

  isAdditionalVerificationRequired(params) {
    return params.isDocumentsRequired;
  },
};

const BrightePayStrategy: IncomeVerificationStrategy = {
  isIncomeVerificationRequired(params) {
    const {
      financeAmount,
      employmentType,
      occupationIncomeThreshold,
      pensionerType,
      dateOfBirth,
      applicantPrimaryIncomeAmount,
      applicantPrimaryIncomePeriod,
    } = params;
    const isWithinThreshold = Number(financeAmount) <= BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD;
    const applicantPrimaryIncomeInMonthlyPeriod = convertAmountToMonthlyPeriod(
      applicantPrimaryIncomeAmount,
      applicantPrimaryIncomePeriod,
    );
    if (!isWithinThreshold) return true;

    if (employmentType === EmploymentType.PENSIONER) {
      return isPensionerIncomeValidationRequired(pensionerType!, dateOfBirth, applicantPrimaryIncomeInMonthlyPeriod);
    }

    return occupationIncomeThreshold ? occupationIncomeThreshold < applicantPrimaryIncomeInMonthlyPeriod : true;
  },

  isAdditionalVerificationRequired(params) {
    const {
      financeAmount,
      employmentType,
      pensionerType,
      dateOfBirth,
      applicantPrimaryIncomeAmount,
      applicantPrimaryIncomePeriod,
    } = params;
    const isWithinThreshold = Number(financeAmount) <= BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD;

    return (
      params.isIncomeVerificationRequired &&
      !(
        isWithinThreshold &&
        (employmentType !== EmploymentType.PENSIONER ||
          !isPensionerIncomeValidationRequired(
            pensionerType!,
            dateOfBirth,
            convertAmountToMonthlyPeriod(applicantPrimaryIncomeAmount, applicantPrimaryIncomePeriod),
          ))
      )
    );
  },
};

const Electrify2515Strategy: IncomeVerificationStrategy = {
  isIncomeVerificationRequired(params) {
    const { isCashApplication, isDocumentsRequired } = params;

    return isCashApplication ? false : isDocumentsRequired;
  },

  isAdditionalVerificationRequired(params) {
    const { isCashApplication, isDocumentsRequired } = params;

    return isCashApplication ? false : isDocumentsRequired;
  },
};

// Strategy registry
export const INCOME_VERIFICATION_STRATEGIES = {
  [productIds.BRIGHTE_PAY]: BrightePayStrategy,
  [productIds.ELECTRIFY_2515]: Electrify2515Strategy,
  DEFAULT: DefaultStrategy,
} as const;
